import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { logError } from '@ptek/client-logger';
import React, {
  FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useWidgetContext } from '@ptek/widget-core';
import * as audioRecorder from '../../../api-clients/audioRecorder';
import {
  persistRecordingLeaseId,
  readOrCreatePersistedRecordingDeviceId,
  readPersistedRecordingLeaseId,
} from '../../../api-clients/recordingLeaseStorage';
import { sessionsClient } from '../../../api-clients/sessions';
import { AppContext } from '../../../context/AppContext';
import { STATUS_CONFIGS } from '../../../constants/statusColors';
import { FEATURE_FLAGS } from '../../../types/featureFlags';
import { RecordingMode } from '../../../types/recorder';
import { AmbientScribeContext } from '../../../types/widget';
import { isFeatureEnabled } from '../../../utils/feature-util';
import {
  loadUserSettingsPreferences,
  saveUserSettingsPreferences,
} from '../../../utils/userPreferences';

/**
 * Maps recording status to theme status key for accessing theme.palette.custom.status
 */
const STATUS_TO_THEME_KEY: Record<string, string> = {
  READY: 'ready',
  STARTING: 'starting',
  TRANSCRIBING: 'transcribing',
  PAUSED: 'paused',
  COMPLETE: 'completed',
  NO_AUDIO: 'warning',
};

/**
 * Maps recording status to Typography variant
 */
const STATUS_TO_VARIANT: Record<
  string,
  | 'statusReady'
  | 'statusStarting'
  | 'statusTranscribing'
  | 'statusPaused'
  | 'statusCompleted'
  | 'statusWarning'
> = {
  READY: 'statusReady',
  STARTING: 'statusStarting',
  TRANSCRIBING: 'statusTranscribing',
  PAUSED: 'statusPaused',
  COMPLETE: 'statusCompleted',
  NO_AUDIO: 'statusWarning',
};
import { useGeneralizedSettings } from '../../../hooks/useGeneralizedSettings';
import { useRecordingState, useRecordingStore } from '../../../stores';
import { HEALTH_STATUS_TYPES } from '../../../types/healthStatus';
import { TranscriptLine } from '../../../types/recorder';
import { useConsentFlow } from '../../Dialogs/hooks/useConsentFlow';
import { useSessionDiscardFlow } from '../../Dialogs/hooks/useSessionDiscardFlow';
import { useConnectionRetry } from '../hooks/useConnectionRetry';
import { useRecording } from '../hooks/useRecording';
import { useStopRecordingStateMachine } from '../hooks/useStopRecordingStateMachine';
import {
  supportsTelehealthCapture,
  useTelehealthCapture,
} from '../hooks/useTelehealthCapture';
import {
  CONNECTION_STATE,
  isDisabledConnectionState,
} from '../stores/connectionRetryStore';
import { STOP_RECORDING_STATE } from '../stores/stopRecordingStore';
import { TRACK_EVENTS } from '../../../userpilot';

import {
  IconCircleSecondary,
  VerticalDivider,
} from '../../../components/LayoutBoxes';
import { recorderStyles as styles } from '../styles/recorderStyles';
import { RecorderSettings } from './RecorderSettings';
import { WaveformVisualizer } from './WaveformVisualizer';

interface RecorderControlBarProps {
  onLogout?: () => void;
  showLogout?: boolean;
  handleTabChange: (tab: string) => void;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  READY: null,
  STARTING: null,
  TRANSCRIBING: (
    <Box
      sx={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: 'custom.status.transcribing.text',
      }}
    />
  ),
  PAUSED: (
    <PauseIcon sx={{ fontSize: '12px', color: 'custom.status.paused.text' }} />
  ),
  COMPLETE: (
    <CheckIcon
      sx={{ fontSize: '12px', color: 'custom.status.completed.text' }}
    />
  ),
  NO_AUDIO: (
    <Box
      sx={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: 'custom.status.warning.text',
      }}
    />
  ),
};

const ICONS = {
  mic: <MicIcon />,
  error: <ErrorOutlineIcon />,
  pause: <PauseIcon />,
  play: <PlayArrowIcon />,
  stop: <StopIcon />,
  time: <AccessTimeIcon />,
} as const;

type ResumeConfirmationReason = 'provider' | 'device' | 'both' | 'unknown';

const RESUME_CONFIRMATION_COPY: Record<ResumeConfirmationReason, string> = {
  provider:
    "This encounter's Scribe session was last recorded by another provider. Continue this session?",
  device:
    "This encounter's Scribe session was last recorded on another computer. Continue this session?",
  both: "This encounter's Scribe session was last recorded by another provider on another computer. Continue this session?",
  unknown:
    "This encounter's Scribe session was previously recorded. Continue this session?",
};

/**
 * Converts a string with newline characters (\n) to a React element with proper line breaks
 */
const formatTooltipText = (text: string): string | React.ReactElement => {
  if (!text.includes('\n')) {
    return text;
  }

  return (
    <Box component='div' sx={styles.tooltipPreLine}>
      {text}
    </Box>
  );
};

const RECORDING_MODE_LABELS: Record<RecordingMode, string> = {
  inPerson: 'Start Transcribing',
  telehealth: 'Start Telehealth',
};

const RECORDING_MODE_ICONS: Record<RecordingMode, React.ReactNode> = {
  inPerson: ICONS.mic,
  telehealth: ICONS.mic,
};

interface RecordingModeButtonProps {
  recordingMode: RecordingMode;
  disabled: boolean;
  label: string;
  testId: string;
  onStart: (recordingMode?: RecordingMode) => void;
  onSelectMode: (recordingMode: RecordingMode) => void;
}

const RecordingModeButton: FC<RecordingModeButtonProps> = ({
  recordingMode,
  disabled,
  label,
  testId,
  onStart,
  onSelectMode,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const alternateMode: RecordingMode =
    recordingMode === 'telehealth' ? 'inPerson' : 'telehealth';

  const handleModeMenuClose = () => setMenuAnchor(null);

  const handleModeSelect = () => {
    onSelectMode(alternateMode);
    handleModeMenuClose();
    onStart(alternateMode);
  };

  return (
    <>
      <ButtonGroup
        sx={styles.recordingModeGroup}
        variant='contained'
        disableElevation
      >
        <Button
          data-testid={testId}
          onClick={() => onStart()}
          disabled={disabled}
          variant='transcribe'
          startIcon={RECORDING_MODE_ICONS[recordingMode]}
          sx={styles.recordingModePrimaryButton}
        >
          {label}
        </Button>
        <Button
          data-testid='recorder-mode-menu'
          aria-label='Choose visit type'
          aria-expanded={Boolean(menuAnchor)}
          disabled={disabled}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          variant='transcribe'
          sx={styles.recordingModeToggleButton}
        >
          <KeyboardArrowDownIcon />
        </Button>
      </ButtonGroup>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleModeMenuClose}
        sx={styles.menu}
        anchorOrigin={styles.menuMainOrigins.anchorOrigin}
        transformOrigin={styles.menuMainOrigins.transformOrigin}
      >
        <MenuItem
          data-testid={`recorder-mode-option-${alternateMode}`}
          onClick={handleModeSelect}
        >
          <ListItemIcon>{RECORDING_MODE_ICONS[alternateMode]}</ListItemIcon>
          <ListItemText>{RECORDING_MODE_LABELS[alternateMode]}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

/**
 * Determines if the recording is in a stopping or error state
 */
const isInStoppingOrErrorState = (
  stateMachine: ReturnType<typeof useStopRecordingStateMachine> | null
): boolean => {
  if (!stateMachine) return false;

  return (
    stateMachine.state === STOP_RECORDING_STATE.STOPPING ||
    stateMachine.state === STOP_RECORDING_STATE.WAITING_TRANSCRIPT ||
    stateMachine.state === STOP_RECORDING_STATE.ERROR_STOPPING ||
    stateMachine.state === STOP_RECORDING_STATE.ERROR_TRANSCRIPT
  );
};

/**
 * Checks if the system is generating a document with content
 */
const isGeneratingWithContent = (
  sessionModel: { isGenerating: boolean; transcript: TranscriptLine[] },
  hasRecordedInThisSession: boolean
): boolean => {
  return (
    sessionModel.isGenerating &&
    (sessionModel.transcript.length > 0 || hasRecordedInThisSession)
  );
};

/**
 * Checks if the system is loading existing session data
 */
const isLoadingExistingSessionData = (
  sessionModel: {
    isGenerating: boolean;
    sessionId: string | null;
  },
  hasRecordedInThisSession: boolean,
  isRecording: boolean
): boolean => {
  return (
    sessionModel.isGenerating &&
    Boolean(sessionModel.sessionId) &&
    !hasRecordedInThisSession &&
    !isRecording
  );
};

/**
 * Determines if the system is in any loading state
 */
const isInLoadingState = (params: {
  isStartingRecording: boolean;
  isStoppingOrWaitingTranscript: boolean;
  isRecording: boolean;
  sessionModel: {
    isGenerating: boolean;
    generationError: boolean;
    bgJobPending: boolean;
    transcript: TranscriptLine[];
    sessionId: string | null;
  };
  hasRecordedInThisSession: boolean;
}): boolean => {
  const {
    isStartingRecording,
    isStoppingOrWaitingTranscript,
    isRecording,
    sessionModel,
    hasRecordedInThisSession,
  } = params;

  // A surfaced generation error is terminal: never show the loading spinner,
  // even if isGenerating lingers true (NEB-96499).
  if (sessionModel.generationError) {
    return false;
  }

  if (!isRecording && sessionModel.bgJobPending) {
    return true;
  }

  if (isStartingRecording || isStoppingOrWaitingTranscript) {
    return true;
  }

  if (
    !isRecording &&
    (isGeneratingWithContent(sessionModel, hasRecordedInThisSession) ||
      isLoadingExistingSessionData(
        sessionModel,
        hasRecordedInThisSession,
        isRecording
      ))
  ) {
    return true;
  }

  return false;
};

const RecorderControlBarComponent: FC<RecorderControlBarProps> = ({
  onLogout,
  showLogout = false,
  handleTabChange,
}) => {
  const {
    availableMicrophones,
    selectedMicrophoneId,
    microphoneError,
    isLoadingMicrophones,
    handleMicrophoneSelect,
    refreshMicrophones,
    selectedFontSize,
    handleFontSizeSelect,
    handleLogout,
  } = useGeneralizedSettings(onLogout);

  const { activeRecordingSession } = useRecordingState();
  const unifiedSessionIdEnabled = useRecordingStore(
    (state) => state.unifiedSessionIdEnabled
  );
  const { sessionModel, user } = useContext(AppContext);
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const continuousCaptureUiEnabled = isFeatureEnabled(
    widgetContext.context?.features,
    FEATURE_FLAGS.CONTINUOUS_CAPTURE_UI
  );
  const telehealthEnabled = isFeatureEnabled(
    widgetContext.context?.features,
    FEATURE_FLAGS.TELEHEALTH
  );
  const practitionerId = user?.username || user?.email;
  const [recordingMode, setRecordingMode] = useState<RecordingMode>('inPerson');
  const [telehealthError, setTelehealthError] = useState<string | null>(null);
  // Gates on the live capability probe, not just the flag: a practitioner on a
  // browser with no getDisplayMedia (Android Chrome, iOS, embedded webviews,
  // non-HTTPS) must never be offered a mode the browser cannot perform
  // (NEB-100902 defect 1/2).
  const telehealthAvailable = useMemo(
    () => telehealthEnabled && supportsTelehealthCapture(),
    [telehealthEnabled]
  );
  const effectiveRecordingMode: RecordingMode = telehealthAvailable
    ? recordingMode
    : 'inPerson';

  useEffect(() => {
    const preferences = loadUserSettingsPreferences(practitionerId);
    setRecordingMode(
      preferences.lastRecordingMode === 'telehealth' ? 'telehealth' : 'inPerson'
    );
  }, [practitionerId]);

  const handleRecordingModeSelect = useCallback(
    (selectedRecordingMode: RecordingMode) => {
      setRecordingMode(selectedRecordingMode);
      setTelehealthError(null);
      // inPerson has no capture step to wait on, so it persists on selection
      // the same way it always has. telehealth persists only once capture
      // actually succeeds (see proceedWithRecording), so a failed or unusable
      // browser never arms an inescapable "Start Telehealth" button on the
      // next visit (NEB-100902 defect 4).
      if (selectedRecordingMode === 'inPerson') {
        saveUserSettingsPreferences(practitionerId, {
          lastRecordingMode: selectedRecordingMode,
        });
      }
    },
    [practitionerId]
  );
  const recordingSessionId = useRecordingStore(
    (state) => state.recordingSessionId
  );
  const isResumableUnifiedSession =
    unifiedSessionIdEnabled &&
    Boolean(recordingSessionId || sessionModel.sessionId) &&
    Boolean(sessionModel.lastTake);
  const isResumeConfirmationEnabled =
    isResumableUnifiedSession && continuousCaptureUiEnabled;

  const { checkPatientConsent } = useConsentFlow();
  const { requestCapture } = useTelehealthCapture();

  const handleRecordingError = useCallback(
    (error: Error) => {
      // Keys off effectiveRecordingMode, not the raw recordingMode state:
      // recordingMode can still hold a stale 'telehealth' preference on a
      // browser where telehealthAvailable is false, in which case the button
      // and the actual recording are both in-person, and this alert would
      // otherwise tell an in-person practitioner to check tab audio for a
      // failure that has nothing to do with telehealth (NEB-100902 defect 3).
      if (effectiveRecordingMode !== 'telehealth') return;

      // The practitioner ending tab sharing mid-visit is not a failed start:
      // the recording ran, and everything captured before the stop is already
      // uploaded. Telling them to "verify tab audio and try again" would read
      // as if the whole visit were lost.
      setTelehealthError(
        error.message === audioRecorder.SHARED_TAB_ENDED_MESSAGE
          ? 'Tab sharing stopped, so the recording ended. Audio captured before that is saved.'
          : 'Telehealth capture could not be started. Verify that tab audio is shared and try again.'
      );
    },
    [effectiveRecordingMode]
  );

  const { checkSessionDiscard, checkResumeOrDiscard } = useSessionDiscardFlow({
    onDocumentCleared: async () => {
      const sessionId = recordingSessionId || sessionModel.sessionId;
      if (!sessionId) return;

      const leaseId =
        audioRecorder.getRecordingLeaseId() ??
        readPersistedRecordingLeaseId(sessionId);
      if (!leaseId) return;

      try {
        await sessionsClient.postRecordingComplete(sessionId, {
          leaseId,
          leaseTakeGeneration: audioRecorder.getTakeGeneration() ?? undefined,
          action: 'stop',
        });
        persistRecordingLeaseId(sessionId, null);
        audioRecorder.setRecordingLeaseId(null);
      } catch (error) {
        logError(
          'RecorderControlBar',
          'Failed to release recording lease; discard continues, and begin-take remains the authoritative conflict gate:',
          error
        );
      }
    },
  });

  const connectionRetry = useConnectionRetry();
  const stopRecordingStateMachine = useStopRecordingStateMachine();

  const {
    isStartingRecording,
    displayDuration,
    completedDuration,
    statusColors,
    isRecording,
    isPaused,
    recordingAudioStream,
    recordingConflict,
    uploadDataLossMessage,
    pauseCheckpointFailed,
    noAudioDetectedMessage,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    trackEvent: trackRecordingEvent,
  } = useRecording({
    activeRecordingSession: activeRecordingSession || null,
    deviceId: selectedMicrophoneId,
    onError: handleRecordingError,
    handleTabChange,
  });

  const isStoppingOrWaitingTranscript = isInStoppingOrErrorState(
    stopRecordingStateMachine
  );

  const [hasRecordedInThisSession, setHasRecordedInThisSession] =
    useState(false);

  const [showWarning, setShowWarning] = useState(false);
  const [resumeConfirmationReason, setResumeConfirmationReason] =
    useState<ResumeConfirmationReason | null>(null);

  useEffect(() => {
    if (isRecording) {
      setHasRecordedInThisSession(true);
    }
  }, [isRecording]);

  useEffect(() => {
    // Key the stop-spinner/loading flag reset on the encounter id (NEB-91362):
    // id is required and always changes per encounter, whereas the recording
    // sessionId is lazily minted undefined→defined mid-recording — keying a
    // reset effect on it would fire mid-recording and miss fresh-encounter
    // switches. The displayed duration is keyed on sessionId separately (and
    // gated on !isRecording); this flag only drives the loading spinner.
    setHasRecordedInThisSession(false);
  }, [activeRecordingSession?.id]);

  useEffect(() => {
    // Clear a pending resume confirmation when the encounter/session switches so
    // a later Continue cannot pair the stale reason with a new session id
    // (NEB-101597).
    setResumeConfirmationReason(null);
  }, [activeRecordingSession?.id]);

  useEffect(() => {
    if (
      sessionModel.healthStatus &&
      sessionModel.healthStatus?.status !== HEALTH_STATUS_TYPES.healthy
    ) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  }, [sessionModel.healthStatus]);

  const isCompleted = useMemo(() => {
    return (
      !isResumableUnifiedSession &&
      !sessionModel.isGenerating &&
      !isRecording &&
      !isStartingRecording &&
      Boolean(sessionModel.documents.length)
    );
  }, [
    sessionModel.isGenerating,
    isRecording,
    isStartingRecording,
    sessionModel.documents.length,
    isResumableUnifiedSession,
  ]);

  const isSessionPaused =
    !isRecording &&
    !sessionModel.activeRecorder &&
    (sessionModel.recordingPaused === true ||
      (sessionModel.lastTake?.pausedAt !== null &&
        sessionModel.lastTake?.pausedAt !== undefined));

  const recordingStatus = useMemo(() => {
    if (isCompleted) return 'COMPLETE';
    if (sessionModel.isGenerating) return 'COMPLETE';
    if (isRecording && isPaused) return 'PAUSED';
    if (isSessionPaused) return 'PAUSED';
    if (isRecording) return 'TRANSCRIBING';
    if (isStartingRecording) return 'STARTING';
    return 'READY';
  }, [
    isRecording,
    isPaused,
    isStartingRecording,
    isCompleted,
    sessionModel.isGenerating,
    isSessionPaused,
  ]);

  const shouldDisableRecording = useMemo(() => {
    return (
      !activeRecordingSession?.patientName ||
      (unifiedSessionIdEnabled && !sessionModel.selectedTemplateId) ||
      Boolean(sessionModel.activeRecorder) ||
      Boolean(recordingConflict)
    );
  }, [
    activeRecordingSession?.patientName,
    sessionModel.selectedTemplateId,
    sessionModel.activeRecorder,
    recordingConflict,
    unifiedSessionIdEnabled,
  ]);

  const proceedWithRecording = useCallback(
    async (selectedRecordingMode: RecordingMode = effectiveRecordingMode) => {
      const shouldProceed = isResumableUnifiedSession
        ? true
        : await checkSessionDiscard(
            sessionModel.transcript.length > 0 || false,
            sessionModel.documents.length > 0 || false
          );

      if (!shouldProceed) {
        return;
      }

      let displayStream: MediaStream | undefined;
      if (telehealthEnabled && selectedRecordingMode === 'telehealth') {
        const captureResult = await requestCapture();
        if (captureResult.status === 'cancelled') {
          setTelehealthError(null);
          return;
        }

        if (captureResult.status === 'failed') {
          setTelehealthError(captureResult.failureMessage);
          return;
        }

        displayStream = captureResult.displayStream;
        // Persist only now that capture actually succeeded (NEB-100902 defect 4).
        saveUserSettingsPreferences(practitionerId, {
          lastRecordingMode: 'telehealth',
        });
      }

      const consentGranted = await checkPatientConsent();

      if (consentGranted) {
        setTelehealthError(null);
        startRecording(displayStream);
      } else {
        displayStream?.getTracks().forEach((track) => track.stop());
      }
    },
    [
      checkSessionDiscard,
      sessionModel.transcript,
      sessionModel.documents.length,
      checkPatientConsent,
      effectiveRecordingMode,
      practitionerId,
      requestCapture,
      startRecording,
      telehealthEnabled,
      isResumableUnifiedSession,
    ]
  );

  const handleStartRecording = useCallback(
    async (selectedRecordingMode?: RecordingMode, resumeConfirmed = false) => {
      if (isResumableUnifiedSession && !resumeConfirmed) {
        const resumeOrDiscard = await checkResumeOrDiscard();

        if (resumeOrDiscard === 'cancel') {
          return;
        }

        if (resumeOrDiscard === 'discard') {
          try {
            await useRecordingStore.getState().callStartFreshUnifiedSession();
          } catch {
            // start-fresh failed and left the old session intact; onError
            // already surfaced it. Abort — do not consent/record on it.
            return;
          }
          // Route through proceedWithRecording rather than consent-then-record:
          // a telehealth take needs its display capture here too, and this
          // branch has isResumableUnifiedSession true so the discard prompt is
          // skipped exactly as an inline consent call would.
          await proceedWithRecording(
            selectedRecordingMode ?? effectiveRecordingMode
          );
          return;
        }
      }

      if (
        isResumableUnifiedSession &&
        isResumeConfirmationEnabled &&
        !resumeConfirmed
      ) {
        const lastTake = sessionModel.lastTake;
        const currentDeviceId = readOrCreatePersistedRecordingDeviceId();
        const providerMismatch = Boolean(
          lastTake?.userId && user?.userId && lastTake.userId !== user.userId
        );
        const deviceMismatch = Boolean(
          lastTake?.deviceId &&
            currentDeviceId &&
            lastTake.deviceId !== currentDeviceId
        );
        const deviceComparisonUnavailable = Boolean(
          lastTake?.deviceId && !currentDeviceId
        );

        let reason: ResumeConfirmationReason | null = null;
        if (!lastTake || deviceComparisonUnavailable) {
          reason = 'unknown';
        } else if (providerMismatch && deviceMismatch) {
          reason = 'both';
        } else if (providerMismatch) {
          reason = 'provider';
        } else if (deviceMismatch) {
          reason = 'device';
        }

        if (reason) {
          if (!resumeConfirmationReason) {
            setResumeConfirmationReason(reason);
            trackRecordingEvent?.(
              TRACK_EVENTS.RECORDING_RESUME_CONFIRMATION_SHOWN,
              {
                sessionId: recordingSessionId || sessionModel.sessionId,
                resumeConfirmationReason: reason,
              }
            );
          }
          return;
        }
      }

      // Clear any pending resume confirmation before starting so the alert does
      // not linger on screen once recording begins (NEB-101597).
      setResumeConfirmationReason(null);
      await proceedWithRecording(
        selectedRecordingMode ?? effectiveRecordingMode
      );
    },
    [
      effectiveRecordingMode,
      isResumableUnifiedSession,
      isResumeConfirmationEnabled,
      checkResumeOrDiscard,
      proceedWithRecording,
      recordingSessionId,
      resumeConfirmationReason,
      sessionModel.lastTake,
      sessionModel.sessionId,
      trackRecordingEvent,
      user?.userId,
    ]
  );

  const handleConfirmResume = useCallback(() => {
    if (!resumeConfirmationReason) return;

    trackRecordingEvent?.(
      TRACK_EVENTS.RECORDING_RESUME_CONFIRMATION_CONFIRMED,
      {
        sessionId: recordingSessionId || sessionModel.sessionId,
        resumeConfirmationReason,
      }
    );
    setResumeConfirmationReason(null);
    void proceedWithRecording(effectiveRecordingMode);
  }, [
    effectiveRecordingMode,
    recordingSessionId,
    proceedWithRecording,
    resumeConfirmationReason,
    sessionModel.sessionId,
    trackRecordingEvent,
  ]);

  const handleCancelResume = useCallback(() => {
    if (!resumeConfirmationReason) return;

    trackRecordingEvent?.(
      TRACK_EVENTS.RECORDING_RESUME_CONFIRMATION_CANCELLED,
      {
        sessionId: recordingSessionId || sessionModel.sessionId,
        resumeConfirmationReason,
      }
    );
    setResumeConfirmationReason(null);
  }, [
    recordingSessionId,
    resumeConfirmationReason,
    sessionModel.sessionId,
    trackRecordingEvent,
  ]);

  const loadingElement = useMemo(
    () => (
      <IconButton disabled variant='recording'>
        <CircularProgress size={24} color='inherit' />
      </IconButton>
    ),
    []
  );

  const getTranscribeButtonConfig = useMemo(() => {
    if (connectionRetry && !isRecording && !isCompleted) {
      const { buttonConfig } = connectionRetry;

      if (isDisabledConnectionState(connectionRetry.connectionState)) {
        const isError =
          connectionRetry.connectionState === CONNECTION_STATE.ERROR;

        return {
          type: 'text' as const,
          text: buttonConfig.label,
          icon: isError ? ICONS.error : ICONS.mic,
          onClick: handleStartRecording,
          disabled: !buttonConfig.enabled || shouldDisableRecording,
          tooltip: buttonConfig.tooltip,
          color: isError ? ('error' as const) : ('primary' as const),
          testId: 'recorder-connection-retry',
        };
      }
    }

    if (isCompleted) {
      return {
        type: 'text' as const,
        text: RECORDING_MODE_LABELS[effectiveRecordingMode],
        icon: ICONS.mic,
        onClick: handleStartRecording,
        disabled: shouldDisableRecording,
        variant: 'outlined' as const,
        testId: 'recorder-start-new',
      };
    }

    if (
      isInLoadingState({
        isStartingRecording,
        isStoppingOrWaitingTranscript,
        isRecording,
        sessionModel,
        hasRecordedInThisSession,
      })
    ) {
      return {
        type: 'loading' as const,
        element: loadingElement,
      };
    }

    if (!isRecording && !isStartingRecording && !sessionModel.isGenerating) {
      return {
        type: 'text' as const,
        // A paused session is being resumed, so the mode label ("Start
        // Telehealth") would misdescribe the action; the take already has its
        // capture mode.
        text: isSessionPaused
          ? 'Resume Transcribing'
          : RECORDING_MODE_LABELS[effectiveRecordingMode],
        icon: isSessionPaused ? ICONS.play : ICONS.mic,
        onClick: handleStartRecording,
        disabled: shouldDisableRecording,
        testId: 'recorder-start',
      };
    }

    if (isRecording && !isPaused) {
      return {
        type: 'dual' as const,
        leftButton: {
          icon: ICONS.pause,
          onClick: pauseRecording,
          variant: 'recording' as const,
          color: 'secondary' as const,
          testId: 'recorder-pause',
        },
        rightButton: {
          icon: ICONS.stop,
          onClick: () => stopRecording(),
          variant: 'recording' as const,
          color: 'error' as const,
          testId: 'recorder-stop',
        },
      };
    }

    if (isRecording && isPaused) {
      return {
        type: 'dual' as const,
        leftButton: {
          icon: ICONS.play,
          onClick: resumeRecording,
          variant: 'recording' as const,
          color: 'primary' as const,
          testId: 'recorder-resume',
        },
        rightButton: {
          icon: ICONS.stop,
          onClick: () => stopRecording(),
          variant: 'recording' as const,
          color: 'warning' as const,
          testId: 'recorder-stop',
        },
      };
    }

    return {
      type: 'text' as const,
      text: RECORDING_MODE_LABELS[effectiveRecordingMode],
      icon: ICONS.mic,
      onClick: handleStartRecording,
      disabled: shouldDisableRecording,
      testId: 'recorder-start',
    };
  }, [
    connectionRetry,
    isCompleted,
    isRecording,
    isPaused,
    isStartingRecording,
    isStoppingOrWaitingTranscript,
    sessionModel,
    isSessionPaused,
    hasRecordedInThisSession,
    shouldDisableRecording,
    handleStartRecording,
    effectiveRecordingMode,
    pauseRecording,
    resumeRecording,
    stopRecording,
    loadingElement,
  ]);

  const shouldShowModeMenu =
    telehealthAvailable &&
    getTranscribeButtonConfig.type === 'text' &&
    (getTranscribeButtonConfig.testId === 'recorder-start' ||
      getTranscribeButtonConfig.testId === 'recorder-start-new');

  const getStatusConfig = useMemo(() => {
    const pillStatus =
      recordingStatus === 'TRANSCRIBING' && noAudioDetectedMessage
        ? 'NO_AUDIO'
        : recordingStatus;
    const config = STATUS_CONFIGS[pillStatus];
    const themeKey = STATUS_TO_THEME_KEY[pillStatus] || 'ready';
    const variant = STATUS_TO_VARIANT[pillStatus] || 'statusReady';

    if (config) {
      return {
        ...config,
        text:
          pillStatus === 'TRANSCRIBING' &&
          effectiveRecordingMode === 'telehealth'
            ? 'TRANSCRIBING TELEHEALTH'
            : config.text,
        icon: STATUS_ICONS[pillStatus] || null,
        themeKey,
        variant,
      };
    }

    return {
      bg: statusColors.bg,
      textColor: 'background.paper',
      text: pillStatus,
      showIcon: false,
      icon: null,
      themeKey,
      variant,
    };
  }, [
    recordingStatus,
    noAudioDetectedMessage,
    statusColors.bg,
    effectiveRecordingMode,
  ]);

  const transformedMicrophones = useMemo(
    () =>
      availableMicrophones.map((m) => ({
        id: m.deviceId,
        label: m.displayName,
        type: m.type,
        description: m.description,
      })),
    [availableMicrophones]
  );

  const calculatedDuration = useMemo(() => {
    // Active recording → live clock. Otherwise the SELECTED session's stored or
    // cross-reload-hydrated total (keyed on sessionId in the store), falling
    // back to 00:00 when that session has no persisted duration.
    if (isRecording) {
      return displayDuration;
    }

    if (completedDuration) {
      return completedDuration;
    }

    return '00:00';
  }, [isRecording, completedDuration, displayDuration]);

  const hasPatient = Boolean(activeRecordingSession?.patientName);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.twoColumn}>
        <Box sx={styles.leftColumn}>
          <Box sx={styles.patientRow}>
            <IconCircleSecondary size={55}>
              <PersonIcon sx={styles.patientIcon} />
            </IconCircleSecondary>
            <Box sx={styles.patientInfoStack}>
              <Typography
                variant='body1'
                sx={styles.getPatientName(hasPatient)}
              >
                {activeRecordingSession?.patientName || 'Select patient'}
              </Typography>
              <Box
                data-testid='recorder-status'
                sx={{
                  ...styles.statusPill,
                  backgroundColor: `custom.status.${getStatusConfig.themeKey}.bg`,
                  gap: getStatusConfig.showIcon ? '6px' : '0px',
                }}
              >
                {getStatusConfig.showIcon && getStatusConfig.icon}
                <Typography
                  variant={getStatusConfig.variant}
                  sx={styles.statusText}
                >
                  {getStatusConfig.text}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={styles.durationRow}>
            <Box sx={styles.durationSection}>
              {ICONS.time}
              <Typography
                data-testid='recorder-timer'
                variant='body1'
                sx={styles.durationText}
              >
                {calculatedDuration}
              </Typography>
            </Box>

            <VerticalDivider />

            <Box sx={styles.waveformContainer}>
              <WaveformVisualizer
                audioStream={recordingAudioStream}
                isTranscribing={recordingStatus === 'TRANSCRIBING'}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={styles.rightColumn}>
          <Box sx={styles.settingsContainer}>
            <RecorderSettings
              microphones={transformedMicrophones}
              selectedMicrophoneId={selectedMicrophoneId}
              microphoneError={microphoneError}
              isLoadingMicrophones={isLoadingMicrophones}
              onSelectMicrophone={handleMicrophoneSelect}
              onRetryMicrophones={refreshMicrophones}
              selectedFontSize={selectedFontSize}
              onSelectFontSize={handleFontSizeSelect}
              showLogout={showLogout}
              onLogout={handleLogout}
            />
          </Box>

          <Box sx={styles.transcribeButtonContainer}>
            <Box sx={styles.buttonWrapper}>
              {shouldShowModeMenu &&
                getTranscribeButtonConfig.type === 'text' && (
                  <RecordingModeButton
                    recordingMode={recordingMode}
                    disabled={getTranscribeButtonConfig.disabled}
                    label={getTranscribeButtonConfig.text}
                    testId={getTranscribeButtonConfig.testId}
                    onStart={getTranscribeButtonConfig.onClick}
                    onSelectMode={handleRecordingModeSelect}
                  />
                )}

              {!shouldShowModeMenu &&
                getTranscribeButtonConfig?.type === 'text' && (
                  <Tooltip
                    title={
                      getTranscribeButtonConfig.tooltip
                        ? formatTooltipText(getTranscribeButtonConfig.tooltip)
                        : ''
                    }
                    arrow
                    disableHoverListener={!getTranscribeButtonConfig.tooltip}
                    PopperProps={{
                      container: document.body,
                      sx: styles.tooltipPopper,
                    }}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -8],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <span>
                      <Button
                        data-testid={getTranscribeButtonConfig.testId}
                        onClick={() => getTranscribeButtonConfig.onClick()}
                        disabled={getTranscribeButtonConfig.disabled}
                        variant={
                          getTranscribeButtonConfig.variant === 'outlined'
                            ? 'transcribe-outlined'
                            : 'transcribe'
                        }
                        color={getTranscribeButtonConfig.color}
                        startIcon={getTranscribeButtonConfig.icon}
                      >
                        {getTranscribeButtonConfig.text}
                      </Button>
                    </span>
                  </Tooltip>
                )}

              {getTranscribeButtonConfig?.type === 'loading' &&
                getTranscribeButtonConfig.element}

              {getTranscribeButtonConfig?.type === 'dual' &&
                getTranscribeButtonConfig.rightButton && (
                  <Box sx={styles.dualButtonContainer}>
                    {getTranscribeButtonConfig.leftButton && (
                      <IconButton
                        data-testid={
                          getTranscribeButtonConfig.leftButton.testId
                        }
                        onClick={getTranscribeButtonConfig.leftButton.onClick}
                        variant={getTranscribeButtonConfig.leftButton.variant}
                        color={getTranscribeButtonConfig.leftButton.color}
                      >
                        {getTranscribeButtonConfig.leftButton.icon}
                      </IconButton>
                    )}
                    <IconButton
                      data-testid={getTranscribeButtonConfig.rightButton.testId}
                      onClick={getTranscribeButtonConfig.rightButton.onClick}
                      variant={getTranscribeButtonConfig.rightButton.variant}
                      color={getTranscribeButtonConfig.rightButton.color}
                    >
                      {getTranscribeButtonConfig.rightButton.icon}
                    </IconButton>
                  </Box>
                )}
            </Box>
          </Box>
        </Box>
      </Box>

      {telehealthError && (
        <Alert
          severity='error'
          onClose={() => setTelehealthError(null)}
          sx={styles.telehealthErrorAlert}
        >
          {telehealthError}
        </Alert>
      )}

      {recordingConflict && (
        <Alert
          data-testid='recorder-recording-conflict'
          severity='error'
          sx={styles.conflictAlert}
        >
          {recordingConflict}
        </Alert>
      )}

      {isRecording && uploadDataLossMessage && (
        <Alert
          data-testid='recorder-upload-data-loss'
          severity='warning'
          sx={styles.conflictAlert}
        >
          {uploadDataLossMessage}
        </Alert>
      )}

      {isRecording && !isPaused && noAudioDetectedMessage && (
        <Alert
          data-testid='recorder-no-audio-detected'
          severity='warning'
          sx={styles.noAudioAlert}
        >
          {noAudioDetectedMessage}
        </Alert>
      )}

      {isRecording && pauseCheckpointFailed && (
        <Alert
          data-testid='recorder-pause-checkpoint-failed'
          severity='warning'
          sx={styles.conflictAlert}
        >
          {pauseCheckpointFailed}
        </Alert>
      )}

      {sessionModel.activeRecorder && (
        <Alert
          data-testid='recorder-active-session-warning'
          severity='warning'
          sx={styles.conflictAlert}
        >
          {continuousCaptureUiEnabled
            ? 'This encounter is currently recording on another computer. Pause or stop that recording before starting here.'
            : 'This encounter is in use on another device.'}
        </Alert>
      )}

      {resumeConfirmationReason && (
        <Alert
          data-testid='recorder-resume-confirmation'
          severity='warning'
          sx={styles.conflictAlert}
          action={
            <Box sx={styles.resumeConfirmationActions}>
              <Button
                data-testid='recorder-resume-confirm'
                color='inherit'
                size='small'
                onClick={handleConfirmResume}
              >
                Continue
              </Button>
              <Button
                data-testid='recorder-resume-cancel'
                color='inherit'
                size='small'
                onClick={handleCancelResume}
              >
                Cancel
              </Button>
            </Box>
          }
        >
          {RESUME_CONFIRMATION_COPY[resumeConfirmationReason]}
        </Alert>
      )}

      {showWarning && (
        <Alert
          icon={false}
          onClose={() => {
            setShowWarning(false);
          }}
          severity='warning'
          sx={styles.warningAlert}
        >
          We are experiencing slower processing speeds. Note generation may take
          longer than usual.
        </Alert>
      )}
    </Box>
  );
};

export const RecorderControlBar = memo(RecorderControlBarComponent);
